import {
  Building2,
  CalendarDays,
  IndianRupee,
  RotateCw,
  WalletCards,
} from "lucide-react";
import { MyPaymentItem } from "../../../../Redux/customer_kyc/types";

interface Props {
  payments: MyPaymentItem[];
  loading: boolean;
  onRefresh?: (paymentId: string) => void;
  resultStart: number;
}

const getStatusClass = (status?: string) =>
  (status || "pending").toLowerCase().replace(/[^a-z]+/g, "-");

const formatCurrency = (value: number) =>
  `₹${new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}`;

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }),
  };
};

const BankAccVerificationTable = ({
  payments,
  loading,
  onRefresh,
  resultStart,
}: Props) => {
  return (
    <div className="bank-verification-table-card">
      <div
        className="bank-verification-table-scroll"
        role="region"
        aria-label="Bank verification table"
        tabIndex={0}
      >
        <table className="bank-verification-table">
          <colgroup>
            <col className="bank-col-sno" />
            <col className="bank-col-date" />
            <col className="bank-col-transaction" />
            <col className="bank-col-status" />
            <col className="bank-col-details" />
            <col className="bank-col-charges" />
          </colgroup>

          <thead>
            <tr>
              <th>S.NO</th>
              <th>DATE</th>
              <th>TRANSACTION ID</th>
              <th>VERIFICATION STATUS</th>
              <th>BANK DETAILS</th>
              <th>CHARGES</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="bank-verification-empty">
                  Loading...
                </td>
              </tr>
            ) : payments.length > 0 ? (
              payments.map((payment, index) => {
                const details = payment.bankDetails;
                const { date, time } = formatDate(payment.createdAt);

                return (
                  <tr key={payment._id}>
                    <td>
                      <span className="bank-verification-serial">
                        {resultStart + index}
                      </span>
                    </td>

                    <td>
                      <div className="bank-verification-date-cell">
                        <CalendarDays size={18} strokeWidth={2.2} />
                        <div>
                          <strong>{date}</strong>
                          <span>{time}</span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="bank-verification-transaction">
                        <WalletCards size={18} strokeWidth={2.2} />
                        <span>
                          {payment.bavId || payment.paymentNumber || "-"}
                        </span>
                      </div>
                    </td>

                    <td>
                      <span
                        className={`bank-verification-status ${getStatusClass(
                          payment.paymentStatus,
                        )}`}
                      >
                        {payment.paymentStatus}
                      </span>
                      {payment.paymentId &&
                        onRefresh &&
                        payment.paymentStatus !== "Success" &&
                        payment.paymentStatus !== "Failed" && (
                          <button
                            type="button"
                            className="bank-verification-refresh"
                            onClick={() =>
                              onRefresh(payment.paymentId!)
                            }
                            title="Refresh Status"
                          >
                            <RotateCw size={13} />
                            Refresh
                          </button>
                        )}
                    </td>

                    <td>
                      {details ? (
                        <div className="bank-verification-details-card">
                          <div className="bank-verification-bank-title">
                            <div className="bank-verification-bank-icon">
                              <Building2
                                size={18}
                                strokeWidth={2.2}
                              />
                            </div>
                            <div>
                              <strong>{details.bankName}</strong>
                              <span>
                                {details.branchName} Branch
                              </span>
                            </div>
                          </div>

                          <div className="bank-verification-detail-grid">
                            <div>
                              <span>Name in Account</span>
                              <strong>
                                {details.accountHolderName}
                              </strong>
                            </div>
                            <div>
                              <span>Account Type</span>
                              <strong>{details.accountType}</strong>
                            </div>
                            <div>
                              <span>Account Number</span>
                              <strong className="bank-verification-mono">
                                {details.accountNumber}
                              </strong>
                            </div>
                            <div>
                              <span>IFSC</span>
                              <strong className="bank-verification-mono">
                                {details.ifscCode}
                              </strong>
                            </div>
                            <div className="bank-verification-detail-wide">
                              <span>Country</span>
                              <strong className="bank-verification-mono">
                                {details.country || "-"}
                              </strong>
                            </div>
                            {details.status && (
                              <div className="bank-verification-detail-wide">
                                <span>Bank Status</span>
                                <span
                                  className={`bank-verification-status ${getStatusClass(
                                    details.status,
                                  )}`}
                                >
                                  {details.status}
                                </span>
                              </div>
                            )}
                            {payment.kycStatus && (
                              <div className="bank-verification-detail-wide">
                                <span>KYC Status</span>
                                <strong>
                                  {payment.kycStatus.replace(/_/g, " ")}
                                  {payment.kycRegion
                                    ? ` (${payment.kycRegion})`
                                    : ""}
                                </strong>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="bank-verification-mono">-</span>
                      )}
                    </td>

                    <td>
                      <div className="bank-verification-charge">
                        <span className="bank-verification-charge-icon">
                          <IndianRupee
                            size={17}
                            strokeWidth={2.4}
                          />
                        </span>
                        <div>
                          <strong>
                            {formatCurrency(payment.charges)}
                          </strong>
                          <span>
                            {payment.salarPayAmount > 0
                              ? `${formatCurrency(
                                  payment.salarPayAmount,
                                )} (Salar Pay)`
                              : "Verification fee"}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="bank-verification-empty">
                  No bank verification records found for the
                  selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BankAccVerificationTable;
